import "./BoardList.scss";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LocalConfigItem } from "@eweadn-qiankun/shared-types";
import {
  notifyKeyboardBoardProfileChanged,
  notifyMouseBoardProfileChanged,
  resolveEquipmentDeviceKey,
  shouldApplyBoardProfileChangeToDevice,
  subscribeKeyboardBoardProfileChanged,
  supportsEs68OnboardPresetCards,
} from "@eweadn-qiankun/shared-utils";
import { useMessage } from "@eweadn-qiankun/shared-components";

function resolveKeyboardBoardProfileSlot(
  activeConfig: any,
  solutionRovider?: string
): number | string {
  if (!activeConfig) return 0;
  if (solutionRovider === "beiying") {
    return `Config${activeConfig.onboardProfileData + 1}`;
  }
  if (typeof activeConfig.value === "number") {
    return activeConfig.value;
  }
  if (typeof activeConfig.key === "number") {
    return activeConfig.key;
  }
  const slotKey = String(activeConfig.key ?? "");
  const match = slotKey.match(/^Slot(\d+)$/i);
  if (match) {
    return Number(match[1]);
  }
  return 0;
}

function resolveKeyboardBoardListItemSlot(
  item: any,
  fallbackIndex: number
): number {
  if (item && typeof item === "object" && typeof item.value === "number") {
    return item.value;
  }
  if (item && typeof item === "object" && typeof item.index === "number") {
    return item.index;
  }
  return fallbackIndex;
}

const ConfigFile = (props: any) => {
  const { t, i18n } = useTranslation();
  const { useSdkApi, menuType, mouseDeviceInfo } = props;
  const globalSdkApi = useSdkApi();
  const sdkApi = globalSdkApi?.keyboard;
  const { sdkKeyboardBasicApi } = sdkApi || {};
  const sdkMouseApi = globalSdkApi?.mouse;
  // 用于判断华奋达
  const getCurrentEquipmentData = sdkKeyboardBasicApi?.getCurrentEquipmentData;
  const solutionRovider = getCurrentEquipmentData?.()?.solutionRovider;
  const solutionRoviderName =
    (typeof window !== "undefined"
      ? localStorage.getItem("solutionRoviderName")
      : null) || solutionRovider;
  const isBeiyingMouse =
    menuType === "mouse" && solutionRoviderName === "beiying";
  const isFreqMouse = menuType === "mouse" && solutionRoviderName === "freq";

  const message = useMessage();
  // 本地配置数据
  const [localConfigs, setLocalConfigs] = useState<LocalConfigItem[]>([]);
  const applyActive = (key: number) => {
    setLocalConfigs((prev) =>
      prev.map((item: any) => ({ ...item, isActive: item.key === key }))
    );
  };

  // 判断是否是鼠标设备
  const isMouseDevice = menuType === "mouse" ? true : false;

  const isZhanliMouse =
    menuType === "mouse" && solutionRoviderName === "zhanli";

  /** 其它方案：名称权威在本地 mouseConfigs；空名不在此烘焙默认文案，渲染时再 t() */
  const buildMouseLocalConfigs = (currentProfile: number) => {
    const savedNames = localStorage.getItem("mouseConfigs");
    const configNames = savedNames ? JSON.parse(savedNames) : {};

    const arr: any = [];
    let profileCount = 4;
    if (isBeiyingMouse || isFreqMouse) profileCount = 1;
    for (let i = 0; i < profileCount; i++) {
      arr.push({
        id: `M${i + 1}`,
        label: t("mouse.boardConfig.localConfigLabel", { num: i + 1 }),
        key: i,
        value: i,
        description: String(
          configNames[i] || configNames[String(i)] || ""
        ).trim(),
        isActive: currentProfile === i,
      });
    }
    return arr as LocalConfigItem[];
  };

  /** 展立：设备读名；空名由渲染层回退「配置文件N」。其它方案勿走此分支。 */
  const buildZhanliMouseConfigs = (currentProfile: number, names: string[]) => {
    const arr: LocalConfigItem[] = [];
    for (let i = 0; i < 4; i++) {
      const deviceName = String(names[i] ?? "").trim();
      arr.push({
        id: `M${i + 1}`,
        label: t("mouse.boardConfig.localConfigLabel", { num: i + 1 }),
        key: i,
        value: i,
        description: deviceName,
        isActive: currentProfile === i,
      });
    }
    return arr;
  };

  const readZhanliNamesCache = (): string[] => {
    try {
      const raw = localStorage.getItem("mouseConfigs");
      if (!raw) return ["", "", "", ""];
      const map = JSON.parse(raw) as Record<string, string>;
      return [0, 1, 2, 3].map((i) =>
        String(map[String(i)] ?? map[i as unknown as string] ?? "").trim()
      );
    } catch {
      return ["", "", "", ""];
    }
  };

  // 获取鼠标配置列表
  const getMouseConfigList = async () => {
    try {
      const profileFromProps =
        typeof mouseDeviceInfo?.profile === "number"
          ? mouseDeviceInfo.profile
          : 0;

      // 展立：打开菜单立刻用缓存/「配置文件N」上屏，再后台校准
      if (isZhanliMouse) {
        setLocalConfigs(
          buildZhanliMouseConfigs(profileFromProps, readZhanliNamesCache())
        );
      }

      // 1) 优先从主应用中间层 getState 获取当前 profile（避免 UI 直连 HIDHandle）
      let currentProfile: number | undefined = undefined;
      try {
        const res = await sdkMouseApi?.sdkMouseBasicApi?.getState?.();
        if (
          res?.code === 200 &&
          res?.data &&
          typeof res.data.profile === "number"
        ) {
          currentProfile = res.data.profile;
        }
      } catch {
        // ignore
      }

      // 2) 其次从 props 的 mouseDeviceInfo（子应用 store）读取
      if (
        currentProfile === undefined &&
        typeof mouseDeviceInfo?.profile === "number"
      ) {
        currentProfile = mouseDeviceInfo.profile;
      }

      if (currentProfile === undefined) {
        currentProfile = 0;
      }
      if (isBeiyingMouse) currentProfile = 0;
      if (isFreqMouse) currentProfile = 0;

      // 展立：后台 0x1E 校准名称与高亮
      if (isZhanliMouse) {
        const res = await sdkMouseApi?.sdkMouseOtherApi?.syncConfigNames?.();
        if (res?.code === 200 && Array.isArray(res.data)) {
          setLocalConfigs(
            buildZhanliMouseConfigs(currentProfile, res.data as string[])
          );
          return;
        }
        setLocalConfigs((prev) =>
          prev.length > 0
            ? prev.map((item) => ({
                ...item,
                isActive: item.key === currentProfile,
              }))
            : buildZhanliMouseConfigs(currentProfile, readZhanliNamesCache())
        );
        return;
      }

      setLocalConfigs(buildMouseLocalConfigs(currentProfile));
    } catch (error) {
      console.error("Failed to get mouse config list:", error);
      // 即使失败也渲染默认板载项，避免弹窗全空
      if (isZhanliMouse) {
        setLocalConfigs(buildZhanliMouseConfigs(0, readZhanliNamesCache()));
      } else {
        setLocalConfigs(buildMouseLocalConfigs(0));
      }
    }
  };

  // 获取键盘配置列表
  const getKeyboardConfigList = async () => {
    if (!sdkKeyboardBasicApi?.getConfigList) {
      console.warn("Keyboard SDK API not available");
      return;
    }

    try {
      const result = await sdkKeyboardBasicApi.getConfigList();
      if (result.code === 200) {
        const configs: LocalConfigItem[] = [];
        const activeConfig = await getConfig();
        // const currentProfile = activeConfig?.key ?? 0;
        const currentProfile = resolveKeyboardBoardProfileSlot(
          activeConfig,
          solutionRovider
        );
        for (let i = 0; i < result.data.list.length; i++) {
          const item = result.data.list[i];
          const slotIndex = resolveKeyboardBoardListItemSlot(item, i);
          const nameData = await getConfigName(i);
          const activeByKey = currentProfile === slotIndex;
          const activeByValue =
            typeof item === "object" &&
            item != null &&
            currentProfile === item.value;
          const activeByConfig = currentProfile === `Config${i + 1}`;
          const activeByListFlag =
            typeof item === "object" && item != null && item.isActive === true;

          configs.push({
            id: `M${i + 1}`,
            label: t("mouse.boardConfig.localConfigLabel", { num: i + 1 }),
            key: slotIndex,
            value:
              typeof item === "object" &&
              item != null &&
              item.value !== undefined
                ? item.value
                : item,
            description: nameData || "",
            isActive:
              activeByKey ||
              activeByValue ||
              activeByConfig ||
              activeByListFlag,
          });
        }

        setLocalConfigs(configs);
      }
    } catch (error) {
      console.error("Failed to get keyboard config list:", error);
    }
  };

  // 获取配置列表（适配键盘和鼠标）
  const getConfigList = async () => {
    if (isMouseDevice) {
      await getMouseConfigList();
    } else {
      await getKeyboardConfigList();
    }
  };
  const getConfig = async () => {
    if (!sdkKeyboardBasicApi?.getConfig) return null;

    try {
      const result = await sdkKeyboardBasicApi.getConfig();
      if (result.code === 200) {
        return result.data;
      }
    } catch (error) {
      console.error("Failed to get config:", error);
    }
    return null;
  };
  // 获取配置名称
  const getConfigName = async (key: any) => {
    if (!sdkKeyboardBasicApi?.getConfigName) return "";

    try {
      const params = {
        index: key,
      };
      const result = await sdkKeyboardBasicApi.getConfigName(params);
      if (result.code === 200) {
        return result.data;
      }
    } catch (error) {
      console.error("Failed to get config name:", error);
    }
    return "";
  };
  const setConfig = async (params: any, type = false) => {
    if (!sdkKeyboardBasicApi?.setConfig) {
      throw new Error("Keyboard SDK API not available");
    }
    return await sdkKeyboardBasicApi.setConfig(params, type);
  };

  const handleConfigClick = async (data: any) => {
    if (isMouseDevice) {
      if (data?.isActive) return;
      // 乐观高亮；展立 SDK 会 last-wins 合并连点，勿在此丢弃后续点击
      applyActive(data.key);
      try {
        // 优先走主应用中间层，避免刷新时 HIDHandle 尚未注入导致无法切换
        const res = sdkMouseApi?.sdkMouseOtherApi?.setProfile
          ? await sdkMouseApi.sdkMouseOtherApi.setProfile({
              value: data.key,
            })
          : null;
        const success = res?.code === 200;
        const finalKey =
          success && typeof res?.data?.profile === "number"
            ? res.data.profile
            : data.key;

        if (success) {
          applyActive(finalKey);
          // 仅最终落到本次数的点击弹 Toast，避免连点重复提示
          if (finalKey === data.key) {
            const item = localConfigs.find((cfg: any) => cfg.key === finalKey);
            notifyMouseBoardProfileChanged(finalKey, {
              name:
                item?.description ||
                item?.label ||
                data.description ||
                data.label,
              source: "user",
            });
          }
        } else {
          // 设备不在线或下发失败时提示失败，不更新 UI
          message.error(t("menu.boardList.switchFailed"));
        }
      } catch (error) {
        console.error("Failed to switch mouse profile:", error);
        message.error(t("menu.boardList.switchFailed"));
      }
    } else {
      // 键盘切换配置
      const params = {
        config: data?.value,
        key: data?.key,
      };
      const result: any = await setConfig(
        params,
        solutionRovider === "beiying" ? true : false
      );
      if (result.code === 200) {
        applyActive(data.key);
        try {
          localStorage.setItem("kb-config", String(data.key));
        } catch (e) {
          console.warn(
            "Failed to save keyboard config index to localStorage",
            e
          );
        }
        notifyKeyboardBoardProfileChanged(data.key, {
          name: data.description || data.label,
          source: "user",
        });
      }
    }
  };

  // 键盘 / 鼠标：切换菜单类型、语言或打开弹窗时拉一次列表
  useEffect(() => {
    void getConfigList();
  }, [isMouseDevice, i18n.language]);

  // 键盘板载变化：事件更新高亮
  useEffect(() => {
    if (isMouseDevice) return;
    return subscribeKeyboardBoardProfileChanged((detail) => {
      const eventDeviceKey = detail.deviceKey;
      let currentDeviceKey = "";
      try {
        const raw = localStorage.getItem("equipmentData");
        if (raw) {
          currentDeviceKey = resolveEquipmentDeviceKey(
            JSON.parse(raw) as Record<string, unknown>
          );
        }
      } catch {
        /* ignore */
      }
      if (
        !shouldApplyBoardProfileChangeToDevice(eventDeviceKey, currentDeviceKey)
      ) {
        return;
      }
      applyActive(detail.key);
    });
  }, [isMouseDevice]);

  // 鼠标板载变化：切换高亮；应用/改名时 detail.name 同步侧边栏描述
  useEffect(() => {
    if (!isMouseDevice) return;
    const syncFromEvent = (e: Event) => {
      const d = (
        e as CustomEvent<{
          profile?: number;
          name?: string;
          deviceKey?: string;
        }>
      )?.detail;
      const p = d?.profile;
      if (typeof p !== "number" || Number.isNaN(p)) return;
      let currentDeviceKey = "";
      try {
        const raw = localStorage.getItem("equipmentData");
        if (raw) {
          currentDeviceKey = resolveEquipmentDeviceKey(
            JSON.parse(raw) as Record<string, unknown>
          );
        }
      } catch {
        /* ignore */
      }
      if (
        !shouldApplyBoardProfileChangeToDevice(d?.deviceKey, currentDeviceKey)
      ) {
        return;
      }
      const hasName = typeof d?.name === "string";
      const rawName = hasName ? String(d?.name ?? "").trim() : undefined;
      setLocalConfigs((prev) =>
        prev.map((item) => {
          const next: LocalConfigItem = {
            ...item,
            isActive: item.key === p,
          };
          if (hasName && item.key === p) {
            // 空名不烘焙默认文案，留给渲染层按当前语言 t()
            next.description = rawName || "";
          }
          return next;
        })
      );
    };
    window.addEventListener("mouse-board-profile-changed", syncFromEvent);
    window.addEventListener("updateMouseUI", syncFromEvent);
    return () => {
      window.removeEventListener("mouse-board-profile-changed", syncFromEvent);
      window.removeEventListener("updateMouseUI", syncFromEvent);
    };
  }, [isMouseDevice, t]);

  // const [es68Description] = useState<any>({
  //   M1: "全域均衡模式",
  //   M2: "疾风瞬击模式",
  //   M3: "巅峰裁决模式",
  //   M4: "超频觉醒模式",
  // });
  const [isEs68, setIsEs68] = useState(false);
  const getIsEs68 = () => {
    if (typeof getCurrentEquipmentData !== "function") {
      setIsEs68(false);
      return;
    }
    setIsEs68(supportsEs68OnboardPresetCards(getCurrentEquipmentData()));
  };
  useEffect(() => {
    getIsEs68();
  }, []);

  return (
    <div className="configFile-list">
      {localConfigs.map((item) => (
        <div
          key={item.id}
          className={`configFile-item ${isEs68 ? "es68Card" : ""} ${item.id} ${item.isActive ? "active" : ""}`}
          onClick={() => handleConfigClick(item)}
        >
          <span className="configFile-id">{item.id}</span>
          <span className="configFile-label">
            {t("mouse.boardConfig.localConfigLabel", { num: item.key + 1 })}
          </span>
          <span className="configFile-desc">
            {item.description?.trim() ||
              t("mouse.boardConfig.defaultConfigName", {
                num: item.key + 1,
              })}
          </span>
        </div>
      ))}
    </div>
  );
};
export default ConfigFile;
